import { type AxiosInstance } from 'axios';

import { type DeviceStatus, type DeviceTypeEnum } from '@assets/enums/device-type.emun.ts';

import { handleAddPhotoToFormData } from '../assets/helpers/utils/handle-add-photo-to-form-data.ts';
import { prepareOrderingForBackend } from '../assets/helpers/utils/prepare-ordering-for-backend.ts';
import { type ChallengeConfig } from './challenge-configs-api.ts';
import type {
  DrivingRangeBaysUpdateDto,
  DrivingRangeDetail,
  DrivingRangeSetupDto,
} from './driving-range-types.ts';
import {
  type ChartData,
  type CommonListArgs,
  type IsoDate,
  type Nullable,
  type PaginatedList,
  type SortDirection,
  type UUID,
  type Weather,
} from './types.ts';

export class CoursesApi {
  constructor(private instance: AxiosInstance) {}

  public async getCourses(params: CoursesListArgs) {
    return this.instance
      .get<PaginatedList<CourseListItem>>('/courses/', {
        params: {
          page: params.page,
          search: params.search,
          ordering: prepareOrderingForBackend(params.sortBy, params.sortDirection),
          region_id: params.region_id,
          city: params.city,
          partner_id: params.partner_id,
          page_size: params.page_size,
        },
      })
      .then((res) => res.data);
  }

  public async createCourse(dto: CourseCreateDto) {
    return this.instance
      .post<CourseCreateResponse>('/courses/', this.prepareCourseFormData(dto))
      .then((res) => res.data);
  }

  public async getCourseById(params: CourseIdParams) {
    return this.instance.get<CourseDetail>(`/courses/${params.courseId}/`).then((res) => res.data);
  }

  public async updateCourse(params: CourseUpdateParams) {
    return this.instance
      .patch<CourseDetail>(`/courses/${params.courseId}/`, this.prepareCourseFormData(params))
      .then((res) => res.data);
  }

  public async deleteCourse(params: CourseIdParams) {
    return this.instance.delete<void>(`/courses/${params.courseId}/`).then((res) => res.data);
  }

  public async getCourseHoles(params: CourseIdParams) {
    return this.instance
      .get<CourseHoleListItem[]>(`/courses/${params.courseId}/holes/`)
      .then((res) => res.data);
  }

  /** Replaces configurations for changed holes; an empty array removes assignments. */
  public async updateCourseHoleGameConfigurations({
    courseId,
    ...dto
  }: CourseHoleGameConfigurationsParams) {
    return this.instance
      .patch<CourseHoleListItem[]>(`/courses/${courseId}/holes/game-configurations/`, dto)
      .then((res) => res.data);
  }

  public async getCourseHole(params: CourseHoleParams) {
    return this.instance
      .get<CourseHoleDetail>(`/courses/${params.courseId}/holes/${params.holeId}/`)
      .then((res) => res.data);
  }

  public async updateCourseHole(params: CourseHoleUpdateParams) {
    const { courseId, holeId, ...dto } = params;

    return this.instance
      .patch<CourseHoleListItem>(`/courses/${courseId}/holes/${holeId}/`, dto)
      .then((res) => res.data);
  }

  public async getCourseSummary(params: CourseIdParams) {
    return this.instance
      .get<CourseSummary>(`/courses/${params.courseId}/summary/`)
      .then((res) => res.data);
  }

  public async getCourseDrivingRange({ courseId }: CourseIdParams) {
    return this.instance
      .get<DrivingRangeDetail>(`/courses/${courseId}/driving-range/`)
      .then((res) => res.data);
  }

  /** Creates or resizes the complete floor layout while preserving retained bay assignments. */
  public async setupCourseDrivingRange({
    courseId,
    ...dto
  }: CourseIdParams & DrivingRangeSetupDto) {
    return this.instance
      .put<DrivingRangeDetail>(`/courses/${courseId}/driving-range/`, dto)
      .then((res) => res.data);
  }

  public async updateCourseDrivingRangeBays({
    courseId,
    ...dto
  }: CourseIdParams & DrivingRangeBaysUpdateDto) {
    return this.instance
      .patch<DrivingRangeDetail>(`/courses/${courseId}/driving-range/bays/`, dto)
      .then((res) => res.data);
  }

  private prepareCourseFormData(dto: CourseCreateDto | CourseUpdateDto) {
    const formData = new FormData();

    if ('id_course' in dto) {
      formData.append('id_course', dto.id_course);
    }

    if (dto.partner) {
      formData.append('partner', dto.partner);
    }

    handleAddPhotoToFormData({
      formData,
      value: dto.photo,
      propName: 'photo',
    });

    return formData;
  }
}

export type CoursesListArgs = {
  search?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
  region_id?: number;
  city?: string;
  partner_id?: string;
  page_size?: number;
} & CommonListArgs;

export type CourseIdParams = {
  courseId: string;
};

export type CourseQueryParams = {
  courseId?: string;
};

export type CourseHoleQueryParams = CourseQueryParams & {
  holeId?: string;
};

export type CourseHoleParams = CourseIdParams & {
  holeId: string;
};

export type CourseHoleUpdateDto = {
  device?: Nullable<string>;
  device_point?: Nullable<CourseHoleDevicePoint>;
  camera?: Nullable<string>;
};

export type CourseHoleUpdateParams = CourseHoleParams & CourseHoleUpdateDto;

export type CourseHoleGameConfigurationsDto = {
  holes: { hole_id: string; challenge_config_ids: number[] }[];
};

export type CourseHoleGameConfigurationsParams = CourseIdParams & CourseHoleGameConfigurationsDto;

export type CourseUpdateDto = {
  partner?: string;
  photo: File | string | null;
};

export type CourseUpdateParams = CourseIdParams & CourseUpdateDto;

export type CourseCountry = {
  id: number;
  name: string;
  iso_code: string;
};

export type CoursePartner = {
  id: string;
  legal_name: string;
  logo: Nullable<string>;
};

export type CourseListItem = {
  id: string;
  name: string;
  photo: Nullable<string>;
  country: Nullable<CourseCountry>;
  address: string;
  partner: CoursePartner;
  holes_count: number;
  devices: number;
  cameras: number;
  unique_players: number;
  total_revenue: number;
  games_played: number;
  revenue_chart: ChartData[];
  games_played_chart: ChartData[];
};

export type CourseCreateDto = {
  partner: string;
  id_course: string;
  photo: File | string | null;
};

export type CourseCreateResponse = {
  id: string;
};

export type CourseDetail = {
  id: string;
  country: Nullable<CourseCountry>;
  partner: CourseDetailPartner;
  igolf_course: Nullable<CourseIGolfCourseDetail>;
  revenue_share_percentage: Nullable<string>;
  photo: Nullable<string>;
  is_active: boolean;
  created_at: IsoDate;
  updated_at: IsoDate;
};

export type CourseDetailPartner = {
  id: string;
  partner_id: string;
  legal_name: string;
  country: Nullable<CourseCountry>;
  logo: Nullable<string>;
  business_address: string;
  contact_email: Nullable<string>;
  contact_phone: Nullable<string>;
};

export type CourseIGolfCourseDetail = {
  id_course: string;
  course_name: string;
  active?: boolean;
  address1?: string;
  address2?: string;
  city?: string;
  zip_code?: string;
  latitude?: Nullable<string>;
  longitude?: Nullable<string>;
  email?: string;
  phone?: string;
  url?: string;
  layout_holes?: Nullable<number>;
  layout_total_holes?: Nullable<number>;
  layout_name?: string;
  class_name?: string;
  dress_code?: string;
  id_language?: Nullable<number>;
  id_country_default_language?: Nullable<number>;
  recommend_rating?: Nullable<string>;
  gps_available?: boolean;
  seasonal?: Nullable<boolean>;
  details_loaded: boolean;
  details_loaded_at?: Nullable<IsoDate>;
  gps_markup: Nullable<unknown>;
  holes: CourseIGolfCourseHole[];
  tees: IGolfCourseTee[];
};

export type CourseIGolfCourseHole = {
  id: string;
  hole_number: number;
  men_par?: Nullable<number>;
  wmn_par?: Nullable<number>;
  men_handicap?: Nullable<number>;
  wmn_handicap?: Nullable<number>;
  front_lat?: Nullable<string>;
  front_lon?: Nullable<string>;
  center_lat?: Nullable<string>;
  center_lon?: Nullable<string>;
  back_lat?: Nullable<string>;
  back_lon?: Nullable<string>;
  tee_points?: unknown;
  custom_points?: unknown;
  geometry: IGolfHoleGeometry;
  device: Nullable<CourseHoleDevice>;
  device_point: Nullable<CourseHoleDevicePoint>;
  camera: Nullable<CourseHoleDevice>;
};

export type CourseHoleDetail = Omit<CourseIGolfCourseHole, 'device' | 'camera'> & {
  course: CourseHoleCourse;
  device: Nullable<CourseHoleDeviceDetail>;
  camera: Nullable<CourseHoleDeviceDetail>;
  weather: Nullable<Weather>;
};

export type IGolfHoleGeometry = {
  green?: IGolfGeometryShape[];
  fairway?: IGolfGeometryShape[];
  perimeter?: IGolfGeometryShape[];
  bunkers?: IGolfGeometryShape[];
  teeboxes?: IGolfGeometryShape[];
  central_path?: IGolfGeometryShape[];
  green_center?: IGolfGeometryShape[];
  teebox_centers?: IGolfGeometryShape[];
};

export type IGolfGeometryShape = {
  points: IGolfGeometryPoint[];
};

export type IGolfGeometryPoint = {
  lat: number;
  lon: number;
};

export type IGolfCourseTee = {
  id_course_tee_type?: Nullable<number>;
  id_course_tee_color?: Nullable<number>;
  tee_name?: string;
  gender?: string;
  tee_color_name?: string;
  tee_color_value?: string;
  rating_men?: Nullable<string>;
  rating_women?: Nullable<string>;
  slope_men?: Nullable<number>;
  slope_women?: Nullable<number>;
  yds_total?: Nullable<number>;
  yds_1_to_9?: Nullable<number>;
  yds_10_to_18?: Nullable<number>;
  yds_1_to_18?: Nullable<number>;
  display_order?: number;
};

export type CourseHoleGps = {
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  map_latitude: Nullable<string>;
  map_longitude: Nullable<string>;
};

export type CourseHoleDevicePoint = {
  lat: number;
  lon: number;
};

export type CourseHoleDevice = {
  id: UUID;
  serial_number: string;
  device_type: DeviceTypeEnum;
  hwid: Nullable<string>;
  status: Nullable<DeviceStatus>;
  gps: CourseHoleGps;
  battery: Nullable<number>;
  updated_at: Nullable<IsoDate>;
};

export type CourseHoleDeviceDetail = CourseHoleDevice & {
  temperature: Nullable<number>;
  last_heartbeat: Nullable<IsoDate>;
  last_stream_start: Nullable<IsoDate>;
  gps: CourseHoleGps;
  firmware: Nullable<CourseHoleDeviceFirmware>;
};

export type CourseHoleDeviceFirmware = {
  id: UUID;
  version: Nullable<string>;
};

export type CourseHoleCourse = {
  id: UUID;
  name: string;
  partner: CourseHolePartner;
};

export type CourseHolePartner = {
  id: UUID;
  legal_name: string;
};

export type CourseHoleListItem = {
  id: string;
  hole: number;
  par: number;
  challenge_configs: ChallengeConfig[];
  device: Nullable<CourseHoleDevice>;
  device_point: Nullable<CourseHoleDevicePoint>;
  camera: Nullable<CourseHoleDevice>;
  games_count: number;
  mbg_wins: number;
  player_wins_count: number;
  revenue: number;
  gross_profit: number;
};

export type CourseSummary = {
  revenue: number;
  gross_profit: number;
  revenue_gross_profit_chart: CourseSummaryRevenueGrossProfitPoint[];
  games_per_day: CourseSummaryGamesPerDay;
  mbg_win_rate: string;
  top_players: CourseSummaryTopPlayer[];
};

export type CourseSummaryTopPlayer = {
  id: string;
  username: Nullable<string>;
  photo: Nullable<string>;
  phone: Nullable<string>;
  games_count: number;
  player_wins_count: number;
  revenue: number;
};

export type CourseSummaryRevenueGrossProfitPoint = {
  date: IsoDate;
  revenue: number;
  gross_profit: number;
};

export type CourseSummaryGamesPerDay = {
  total: number;
  max: number;
  min: number;
  chart: CourseSummaryGamesPerDayPoint[];
};

export type CourseSummaryGamesPerDayPoint = {
  date: IsoDate;
  games_count: number;
};
